
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Sparkles, ArrowRight, MapPin, DollarSign, Home, Heart, CheckCircle, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../integrations/supabase/client';
import { toast } from '../hooks/use-toast';

interface OnboardingProps {
  onComplete: () => void;
}

const OnboardingFlow: React.FC<OnboardingProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [preferences, setPreferences] = useState({
    budgetMin: '',
    budgetMax: '',
    bedrooms: 1,
    preferredLocations: [] as string[],
    dealBreakers: [] as string[],
    petFriendly: false,
    preferredAmenities: [] as string[]
  });

  const locationOptions = [
    'Downtown Louisville', 'Highlands', 'St. Matthews', 'Jeffersontown',
    'Bardstown Road', 'Frankfort Avenue', 'NuLu', 'Old Louisville',
    'Cherokee', 'Audubon'
  ];

  const amenityOptions = [
    'parking', 'laundry', 'air_conditioning', 'dishwasher', 'gym', 
    'pool', 'balcony', 'hardwood_floors', 'walk_in_closet'
  ];

  const dealBreakerOptions = [
    'No Pets Allowed', 'No Parking', 'Above 3rd Floor (No Elevator)',
    'Shared Bathroom', 'No A/C', 'Basement Unit', 'No Natural Light'
  ];

  const toggleArrayItem = (array: string[], item: string) => {
    return array.includes(item) 
      ? array.filter(i => i !== item)
      : [...array, item];
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!user) return;

    try {
      // Save preferences to renter_profiles table
      const { error } = await supabase
        .from('renter_profiles')
        .upsert({
          user_id: user.id,
          budget_min: parseInt(preferences.budgetMin) || 500,
          budget_max: parseInt(preferences.budgetMax) || 2000,
          bedrooms: preferences.bedrooms,
          preferred_locations: preferences.preferredLocations,
          preferred_amenities: preferences.preferredAmenities,
          pet_friendly: preferences.petFriendly,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving preferences:', error);
        toast({
          title: "Error saving preferences",
          description: "Please try again or you can update them later in your profile.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Preferences saved! 🎉",
          description: "I'll use these to find your perfect matches. You can update them anytime in your profile.",
        });
      }

      // Mark onboarding as complete
      localStorage.setItem('onboardingComplete', 'true');
      localStorage.setItem('hasSeenOnboarding', 'true');
      onComplete();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error saving preferences",
        description: "Please try again or you can update them later in your profile.",
        variant: "destructive",
      });
      // Still complete onboarding even if preferences save fails
      localStorage.setItem('onboardingComplete', 'true');
      localStorage.setItem('hasSeenOnboarding', 'true');
      onComplete();
    }
  };

  const handleSkipToFiona = () => {
    const hasSkippedToFiona = localStorage.getItem('hasSkippedToFiona');
    
    if (!hasSkippedToFiona) {
      localStorage.setItem('hasSkippedToFiona', 'true');
      localStorage.setItem('hasSeenOnboarding', 'true');
      localStorage.setItem('onboardingComplete', 'true');
      
      window.location.href = '/chat';
    }
  };

  const hasSkippedToFiona = localStorage.getItem('hasSkippedToFiona');

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white p-8 max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
                {step < 4 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step < currentStep ? 'bg-pink-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 text-center">
            Step {currentStep} of 4
          </p>
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <Sparkles className="w-16 h-16 text-pink-500 mx-auto" />
              <h2 className="text-3xl font-bold text-gray-900">
                Welcome to Flamingo! 🦩
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-md mx-auto">
                I'm Fiona, your AI rental concierge. I'll help you find the perfect home by learning your preferences.
              </p>
            </div>
            <Card className="bg-pink-50 border-pink-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-3">What makes Flamingo special?</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-center text-gray-700">
                  <Heart className="w-4 h-4 text-pink-500 mr-3 flex-shrink-0" />
                  <span>Swipe-style property discovery</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Sparkles className="w-4 h-4 text-pink-500 mr-3 flex-shrink-0" />
                  <span>AI-powered matching</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Home className="w-4 h-4 text-pink-500 mr-3 flex-shrink-0" />
                  <span>Direct landlord communication</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <DollarSign className="w-12 h-12 text-pink-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900">What's your budget?</h2>
              <p className="text-gray-600">Let us know your price range</p>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budgetMin" className="text-gray-900 font-medium">Minimum Budget</Label>
                  <Input
                    id="budgetMin"
                    type="number"
                    placeholder="800"
                    value={preferences.budgetMin}
                    onChange={(e) => setPreferences(prev => ({ ...prev, budgetMin: e.target.value }))}
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budgetMax" className="text-gray-900 font-medium">Maximum Budget</Label>
                  <Input
                    id="budgetMax"
                    type="number"
                    placeholder="1500"
                    value={preferences.budgetMax}
                    onChange={(e) => setPreferences(prev => ({ ...prev, budgetMax: e.target.value }))}
                    className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-300"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <Label className="text-gray-900 font-medium text-base">How many bedrooms?</Label>
                <div className="grid grid-cols-5 gap-3">
                  {[0, 1, 2, 3, 4].map((num) => (
                    <Button
                      key={num}
                      variant={preferences.bedrooms === num ? "default" : "outline"}
                      onClick={() => setPreferences(prev => ({ ...prev, bedrooms: num }))}
                      className={`h-12 text-sm font-medium ${
                        preferences.bedrooms === num 
                          ? 'bg-pink-500 hover:bg-pink-600 text-white border-pink-500' 
                          : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {num === 0 ? 'Studio' : `${num} BR`}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <MapPin className="w-12 h-12 text-pink-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900">Where do you want to live?</h2>
              <p className="text-gray-600">Select your preferred areas and amenities</p>
            </div>
            
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-900 font-medium text-base">Preferred Areas</Label>
                  <p className="text-sm text-gray-500">Select multiple areas</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {locationOptions.map((location) => (
                    <Button
                      key={location}
                      variant={preferences.preferredLocations.includes(location) ? "default" : "outline"}
                      onClick={() => setPreferences(prev => ({
                        ...prev,
                        preferredLocations: toggleArrayItem(prev.preferredLocations, location)
                      }))}
                      className={`h-auto p-3 text-left justify-start whitespace-normal text-sm ${
                        preferences.preferredLocations.includes(location)
                          ? 'bg-pink-500 hover:bg-pink-600 text-white border-pink-500'
                          : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {location}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Selected: {preferences.preferredLocations.length} areas
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-900 font-medium text-base">Must-Have Amenities</Label>
                  <p className="text-sm text-gray-500">Select multiple amenities</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {amenityOptions.map((amenity) => (
                    <Button
                      key={amenity}
                      variant={preferences.preferredAmenities.includes(amenity) ? "default" : "outline"}
                      onClick={() => setPreferences(prev => ({
                        ...prev,
                        preferredAmenities: toggleArrayItem(prev.preferredAmenities, amenity)
                      }))}
                      className={`h-auto p-3 text-left justify-start text-sm ${
                        preferences.preferredAmenities.includes(amenity)
                          ? 'bg-pink-500 hover:bg-pink-600 text-white border-pink-500'
                          : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {amenity.replace('_', ' ').split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Selected: {preferences.preferredAmenities.length} amenities
                </p>
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <CheckCircle className="w-12 h-12 text-pink-500 mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900">Deal Breakers & Final Details</h2>
              <p className="text-gray-600">What should we avoid?</p>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-900 font-medium text-base">Deal Breakers</Label>
                  <p className="text-sm text-gray-500">Select what you want to avoid</p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {dealBreakerOptions.map((dealBreaker) => (
                    <Button
                      key={dealBreaker}
                      variant={preferences.dealBreakers.includes(dealBreaker) ? "destructive" : "outline"}
                      onClick={() => setPreferences(prev => ({
                        ...prev,
                        dealBreakers: toggleArrayItem(prev.dealBreakers, dealBreaker)
                      }))}
                      className={`h-auto p-4 text-left justify-start text-sm ${
                        preferences.dealBreakers.includes(dealBreaker)
                          ? 'bg-red-500 hover:bg-red-600 text-white border-red-500'
                          : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {dealBreaker}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Selected: {preferences.dealBreakers.length} deal breakers
                </p>
              </div>

              <Card className="bg-gray-50 border-gray-200 p-6">
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">You're all set! 🎉</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    I'll use these preferences to calculate match scores for each property. The better the match, the higher the percentage!
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-gray-600">
                    <Sparkles className="w-4 h-4" />
                    <span>You can always update these preferences in your profile settings</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-col space-y-4 mt-8 pt-6 border-t border-gray-200 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <Button
              variant="outline"
              onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : null}
              disabled={currentStep === 1}
              className="px-6 bg-white text-gray-900 border-gray-300 hover:bg-gray-50 order-2 sm:order-1"
            >
              Back
            </Button>
            
            {!hasSkippedToFiona && currentStep === 1 && (
              <Button
                variant="outline"
                onClick={handleSkipToFiona}
                className="bg-white border-pink-200 text-pink-600 hover:bg-pink-50 hover:border-pink-300 flex items-center justify-center space-x-2 order-1 sm:order-2"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Skip to Fiona</span>
              </Button>
            )}
          </div>
          
          <Button
            onClick={handleNext}
            disabled={
              (currentStep === 2 && (!preferences.budgetMin || !preferences.budgetMax)) ||
              (currentStep === 3 && preferences.preferredLocations.length === 0)
            }
            className="px-6 bg-pink-500 hover:bg-pink-600 text-white flex items-center justify-center space-x-2"
          >
            <span>{currentStep === 4 ? 'Start Discovering!' : 'Next'}</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default OnboardingFlow;
